import { createFileRoute } from "@tanstack/react-router";

import { Header } from "@/components/site/header";
import { Hero } from "@/components/site/hero";
import { AudienceStrip } from "@/components/site/audience-strip";
import { Categories } from "@/components/site/categories";
import { BusinessSolutions } from "@/components/site/business-solutions";
import { WorkGallery } from "@/components/site/work-gallery";
import { Process } from "@/components/site/process";
import { WhyUs } from "@/components/site/why-us";
import { BrandAssist } from "@/components/site/brand-assist";
import { Story } from "@/components/site/story";
import { Faq } from "@/components/site/faq";
import { FinalCtaFooter } from "@/components/site/footer";
import { DeferredChrome } from "@/components/site/deferred-chrome";
import {
  DeferredProductFinder,
  DeferredQuoteForm,
} from "@/components/site/deferred-island";
import { QuoteProvider } from "@/components/site/quote-context";
import heroImg from "@/assets/hero-engraving.jpg";
import { SITE_NAME, SITE_URL, absoluteUrl } from "@/lib/site";

const TITLE = "Custom Engraving & Promotional Products | Mills Engraving";
const DESCRIPTION =
  "Request a quote for custom-engraved promotional products, business gifts, and event items from Mills Engraving in Manitoba.";
const OG_IMAGE = absoluteUrl(heroImg);
const PAGE_URL = absoluteUrl("/");

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Laser engraving on a brushed metal tumbler" },
      { property: "og:locale", content: "en_CA" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: SITE_NAME,
          url: SITE_URL,
          image: OG_IMAGE,
          description: DESCRIPTION,
          foundingDate: "2019",
          telephone: "+1-204-761-3698",
          email: "millsengraving@gmail.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Stockton",
            addressRegion: "MB",
            addressCountry: "CA",
          },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Sales",
            name: "Steven Wollman",
            telephone: "+1-204-761-3698",
            email: "millsengraving@gmail.com",
          },
          knowsAbout: [
            "Custom engraving",
            "Promotional products",
            "Corporate gifts",
            "Awards and recognition",
          ],
        }),
      },
    ],
  }),
});

function Index() {
  return (
    <QuoteProvider>
      <Header />
      <main>
        <Hero />
        <AudienceStrip />
        <Categories />
        <DeferredProductFinder />
        <BusinessSolutions />
        <WorkGallery />
        <Process />
        <WhyUs />
        <BrandAssist />
        <Story />
        <Faq />
        <DeferredQuoteForm />
      </main>
      <FinalCtaFooter />
      <DeferredChrome />
    </QuoteProvider>
  );
}
